import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import toast from 'react-hot-toast';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { 
    Users, 
    UserPlus, 
    Search, 
    Trash2, 
    Edit2, 
    Mail, 
    Shield, 
    RefreshCw, 
    ChevronsDown,
    X,
    User,
    CheckCircle2
} from 'lucide-react';
import { appImages } from "../../utils/constants"; 

// --- Compact Styles for React Select ---
const selectStyles = {
    control: (styles) => ({ 
        ...styles, 
        backgroundColor: '#27272a', // zinc-800
        borderColor: '#3f3f46', // zinc-700
        boxShadow: 'none', 
        '&:hover': { borderColor: '#52525b' }, // zinc-600
        minHeight: '36px', // Compact height
        borderRadius: '0.375rem', // rounded-md
        fontSize: '0.875rem'
    }),
    menu: (styles) => ({ 
        ...styles, 
        backgroundColor: '#18181b', // zinc-950
        borderColor: '#3f3f46',
        borderWidth: '1px',
        borderRadius: '0.375rem',
        padding: '0.25rem',
        zIndex: 50
    }),
    option: (styles, { isFocused, isSelected }) => ({ 
        ...styles, 
        backgroundColor: isSelected ? '#2563eb' : isFocused ? '#27272a' : '#18181b', 
        color: 'white', 
        cursor: 'pointer',
        fontSize: '0.75rem', // Smaller font
        padding: '0.375rem 0.75rem', 
        borderRadius: '0.25rem'
    }),
    singleValue: (styles) => ({ ...styles, color: '#e4e4e7', fontSize: '0.875rem' }), 
    input: (styles) => ({ ...styles, color: 'white', margin: 0, padding: 0 }),
    valueContainer: (styles) => ({ ...styles, padding: '0 8px' }),
    placeholder: (styles) => ({ ...styles, color: '#a1a1aa', fontSize: '0.875rem' }),
};

// --- UI Helper Components ---

const Avatar = ({ name }) => {
    const initials = name
        ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
        : '??';
    
    const colors = ['bg-blue-600', 'bg-emerald-600', 'bg-purple-600', 'bg-amber-600', 'bg-rose-600'];
    const colorClass = colors[name ? name.length % colors.length : 0];

    return (
        <div className={`w-8 h-8 rounded-full ${colorClass} flex items-center justify-center text-xs font-bold text-white shadow-sm ring-1 ring-zinc-900`}>
            {initials}
        </div>
    );
};

const AppBadge = ({ appKey, data }) => {
    if (!data) return null;
    const appNames = { jira: "Jira", microsoft_teams: "Teams", slack: "Slack", gmail: "Gmail" };

    return (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-zinc-800/80 border border-zinc-700/50 text-[11px] text-zinc-300">
            {appImages[appKey] ? (
                <img src={appImages[appKey]} alt={appKey} className="object-contain w-3 h-3" />
            ) : (
                <div className="w-1.5 h-1.5 rounded-full bg-[#5652CF]"></div>
            )}
            <span className="truncate max-w-[100px]">{data.displayName || data.email || appNames[appKey]}</span>
        </div>
    );
};

const UserTableRowSkeleton = () => (
    <tr className="border-b animate-pulse border-zinc-800/50">
        <td className="px-6 py-3"><div className="w-8 h-8 rounded-full bg-zinc-800"></div></td>
        <td className="px-6 py-3"><div className="w-32 h-4 mb-1 rounded bg-zinc-800"></div><div className="w-20 h-3 rounded bg-zinc-800"></div></td>
        <td className="px-6 py-3"><div className="flex gap-2"><div className="w-16 h-6 rounded bg-zinc-800"></div></div></td>
        <td className="px-6 py-3 text-right"><div className="ml-auto rounded h-7 w-7 bg-zinc-800"></div></td>
    </tr>
);

// --- Main Component ---

const UserManagementContent = () => {
    const { userGoogleId } = DataState();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const [isBulkMapOpen, setIsBulkMapOpen] = useState(false);
    const [mappableAccounts, setMappableAccounts] = useState({ jira_users: [], teams_users: [] });

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [usersRes, mappableRes] = await Promise.all([
                fetch(`${API_URL}/users/`, { headers: { Authorization: `Bearer ${userGoogleId}` }, credentials: 'include', }),
                fetch(`${API_URL}/users/mappable_accounts`, { headers: { Authorization: `Bearer ${userGoogleId}` }, credentials: 'include', })
            ]);
            
            if (!usersRes.ok) throw new Error("Failed to load user list.");
            
            const usersData = await usersRes.json();
            const mappableData = mappableRes.ok ? await mappableRes.json() : { jira_users: [], teams_users: [] };

            setUsers(usersData);
            setMappableAccounts(mappableData);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [userGoogleId]);

    const handleAddUser = () => {
        setEditingUser({ displayName: '', email: '', role: '', app_mappings: {} });
        setIsFormOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsFormOpen(true);
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm("Delete this user? This cannot be undone.")) {
            try {
                const res = await fetch(`${API_URL}/users/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${userGoogleId}` }, credentials: 'include' });
                if (!res.ok) throw new Error("Failed to delete user.");
                toast.success("User deleted.");
                fetchAllData(); 
            } catch (err) {
                toast.error(err.message);
            }
        }
    };

    const filteredUsers = useMemo(() => {
        if (!searchQuery) return users;
        const lowerQuery = searchQuery.toLowerCase();
        return users.filter(user => 
            (user.displayName && user.displayName.toLowerCase().includes(lowerQuery)) ||
            (user.email && user.email.toLowerCase().includes(lowerQuery))
        );
    }, [users, searchQuery]);

    return (
        <div className="flex flex-col h-full max-h-screen p-1">
            {/* --- Compact Header --- */}
            <div className="flex flex-col items-center justify-between gap-4 px-1 mb-4 md:flex-row">
                <div>
                    <h3 className="flex items-center gap-2 text-lg font-bold text-zinc-100">
                        <Users className="w-5 h-5 text-[#5652CF]" />
                        Team Directory
                    </h3>
                    <p className="text-xs text-zinc-500">Manage user access and integrations.</p>
                </div>
                
                <div className="flex items-center w-full gap-2 md:w-auto">
                    <button 
                        onClick={() => fetchAllData()} 
                        disabled={loading}
                        className="p-2 transition-colors border rounded-md text-zinc-400 hover:text-zinc-100 bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <button 
                        onClick={() => setIsBulkMapOpen(true)} 
                        className="flex items-center gap-2 px-3 py-2 text-xs font-medium transition-all border rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-zinc-700 hover:border-zinc-600"
                    >
                        <RefreshCw className="w-3.5 h-3.5" /> Bulk Sync
                    </button>

                    <button 
                        onClick={handleAddUser} 
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-[#5652CF] text-white rounded-md text-xs font-medium shadow-sm transition-all hover:-translate-y-0.5"
                    >
                        <UserPlus className="w-3.5 h-3.5" /> Add User
                    </button>
                </div>
            </div>

            {/* --- Content Table Card --- */}
            <div className="bg-[#121214] border border-zinc-800 rounded-lg overflow-hidden shadow-sm flex flex-col flex-1 min-h-0">
                
                {/* Compact Toolbar */}
                <div className="flex items-center gap-4 px-4 py-3 border-b border-zinc-800 bg-zinc-900/30">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                        <input 
                            type="text" 
                            placeholder="Search..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full py-2 pr-3 text-xs transition-all border rounded-md bg-zinc-900 border-zinc-700 text-zinc-200 pl-9 focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50 focus:border-[#5652CF] placeholder:text-zinc-600"
                        />
                    </div>
                    <div className="hidden ml-auto text-xs text-zinc-500 md:block">
                        <span className="font-semibold text-zinc-300">{users.length}</span> Users
                    </div>
                </div>

                {/* Compact Table */}
                <div className="flex-1 overflow-auto custom-scrollbar bg-zinc-900/10">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10 border-b bg-zinc-900/90 backdrop-blur-sm border-zinc-800">
                            <tr>
                                <th className="px-6 py-3 text-[10px] font-bold uppercase text-zinc-500 tracking-wider w-14">Avatar</th>
                                <th className="px-6 py-3 text-[10px] font-bold uppercase text-zinc-500 tracking-wider">User Details</th>
                                <th className="px-6 py-3 text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Integrations</th>
                                <th className="px-6 py-3 text-[10px] font-bold uppercase text-zinc-500 tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/50">
                            {loading ? (
                                <>
                                    <UserTableRowSkeleton />
                                    <UserTableRowSkeleton />
                                    <UserTableRowSkeleton />
                                </>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="py-16 text-center">
                                        <div className="flex flex-col items-center justify-center text-zinc-500">
                                            <div className="p-3 mb-3 rounded-full bg-zinc-800/50">
                                                <User className="w-6 h-6 opacity-50" />
                                            </div>
                                            <p className="text-sm font-medium text-zinc-400">No users found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map(user => (
                                    <tr key={user.id} className="transition-colors group hover:bg-zinc-800/30">
                                        <td className="px-6 py-3 align-top">
                                            <Avatar name={user.displayName} />
                                        </td>
                                        <td className="px-6 py-3 align-top">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium leading-tight transition-colors text-zinc-200 group-hover:text-blue-400">
                                                    {user.displayName || "Unnamed"}
                                                </span>
                                                <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
                                                    <Mail className="w-3 h-3" />
                                                    {user.email}
                                                </div>
                                                {user.role && (
                                                     <div className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-600 font-bold uppercase tracking-wide">
                                                        <Shield className="w-3 h-3" /> {user.role}
                                                     </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 align-top">
                                            <div className="flex flex-wrap gap-1.5">
                                                {Object.keys(user.app_mappings || {}).length === 0 && (
                                                    <span className="text-xs italic text-zinc-600">No links</span>
                                                )}
                                                {Object.entries(user.app_mappings || {}).map(([key, data]) => (
                                                    <AppBadge key={key} appKey={key} data={data} />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-right align-middle">
                                            <div className="flex items-center justify-end gap-1 transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100">
                                                <button 
                                                    onClick={() => handleEditUser(user)} 
                                                    className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-[#5652CF]/10 rounded-md transition-all"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteUser(user.id)} 
                                                    className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- Modals --- */}
            {isFormOpen && (
                <UserForm 
                    user={editingUser} 
                    allUsers={users}
                    mappableAccounts={mappableAccounts}
                    onClose={() => setIsFormOpen(false)} 
                    onSave={() => { setIsFormOpen(false); fetchAllData(); }}
                />
            )}
            
            {isBulkMapOpen && (
                <BulkMapModal 
                    allUsers={users}
                    mappableTeamsUsers={mappableAccounts.teams_users}
                    onClose={() => setIsBulkMapOpen(false)}
                    onSave={() => { setIsBulkMapOpen(false); fetchAllData(); }}
                />
            )}
        </div>
    );
};

// --- Compact User Form Modal ---
const UserForm = ({ user, allUsers, onClose, onSave, mappableAccounts }) => {
    const { userGoogleId } = DataState();
    const [formData, setFormData] = useState(user);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(''); 

    // Generate role options
    const standardRoles = ["Developer", "Project Manager", "QA Analyst", "Designer", "Admin"];
    const existingRoles = [...new Set(allUsers.map(u => u.role).filter(Boolean))];
    const roleOptions = [...new Set([...standardRoles, ...existingRoles])].map(role => ({ value: role, label: role }));

    useEffect(() => {
        if (!formData.email) {
            setEmailError('');
            return;
        }
        const isDuplicate = allUsers.some(existingUser =>
            existingUser.email.toLowerCase() === formData.email.toLowerCase() &&
            existingUser.id !== user.id
        );
        if (isDuplicate) {
            setEmailError('Email already exists.');
        } else {
            setEmailError('');
        }
    }, [formData.email, allUsers, user.id]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRoleChange = (selectedOption) => {
        setFormData({ ...formData, role: selectedOption ? selectedOption.value : '' });
    };

    const handleMappingChange = (appName, selectedOption) => {
        const newMappings = { ...formData.app_mappings };
        if (selectedOption) {
            const originalUser = selectedOption.originalUser;
            if (appName === 'jira') {
                newMappings.jira = {
                    accountId: originalUser.accountId,
                    displayName: originalUser.displayName,
                    emailAddress: originalUser.emailAddress
                };
            } else if (appName === 'microsoft_teams') {
                newMappings.microsoft_teams = {
                    userId: originalUser.userId,
                    displayName: originalUser.displayName,
                    email: originalUser.email
                };
            }
        } else {
            delete newMappings[appName];
        }
        setFormData({ ...formData, app_mappings: newMappings });
    };

    const handlePrefill = (source) => {
        let prefillData = { displayName: '', email: '' };

        if (source === 'jira' && formData.app_mappings.jira) {
            const jiraUser = mappableAccounts.jira_users.find(u => u.accountId === formData.app_mappings.jira.accountId);
            if (jiraUser) {
                prefillData.displayName = jiraUser.displayName;
                prefillData.email = jiraUser.emailAddress || ''; 
            }
        } else if (source === 'microsoft_teams' && formData.app_mappings.microsoft_teams) {
            const teamsUser = mappableAccounts.teams_users.find(u => u.userId === formData.app_mappings.microsoft_teams.userId);
            if (teamsUser) {
                prefillData.displayName = teamsUser.displayName;
                prefillData.email = teamsUser.email || ''; 
            }
        }

        setFormData(prev => ({
            ...prev,
            displayName: prefillData.displayName,
            email: prefillData.email
        }));
        toast.success(`Form pre-filled.`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const url = user.id ? `${API_URL}/users/${user.id}` : `${API_URL}/users/`;
        const method = user.id ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userGoogleId}` },
                credentials: 'include',
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error("Failed to save user.");
            toast.success(`User saved.`);
            onSave();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const jiraOptions = mappableAccounts.jira_users.map(u => ({
        value: u.accountId,
        label: `${u.displayName} (${u.emailAddress || 'No Email'})`,
        originalUser: u 
    }));

    const teamsOptions = mappableAccounts.teams_users.map(u => ({
        value: u.userId,
        label: `${u.displayName} (${u.email || 'No Email'})`,
        originalUser: u 
    }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 duration-200 bg-black/80 backdrop-blur-sm animate-in fade-in">
            <div className="bg-[#121214] border border-zinc-700 w-full max-w-md rounded-lg shadow-2xl p-6 flex flex-col scale-100 animate-in zoom-in-95">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-zinc-100">{user.id ? "Edit User" : "Add User"}</h3>
                    <button onClick={onClose} className="p-1 transition-colors rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800"><X className="w-4 h-4"/></button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-3">
                        <div>
                            <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1 block">Display Name</label>
                            <input name="displayName" value={formData.displayName} onChange={handleChange} placeholder="Name" className="w-full p-2 text-sm text-white transition-all border rounded-md bg-zinc-900 border-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50 focus:border-[#5652CF]" required />
                        </div>
                        
                        <div>
                            <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1 block">Email</label>
                            <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" className={`w-full bg-zinc-900 border ${emailError ? 'border-red-500' : 'border-zinc-700'} rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:border-[#5652CF] transition-all`} required />
                            {emailError && <p className="mt-1 text-xs text-red-400">{emailError}</p>}
                        </div>

                        <div>
                            <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1 block">Role</label>
                            <CreatableSelect
                                styles={selectStyles}
                                options={roleOptions}
                                value={formData.role ? { value: formData.role, label: formData.role } : null}
                                onChange={handleRoleChange}
                                placeholder="Select role..."
                                isClearable
                            />
                        </div>

                        <div className="pt-4 border-t border-zinc-800/50">
                            <h4 className="mb-3 text-xs font-bold tracking-wider uppercase text-zinc-300">Linked Accounts</h4>
                            
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1.5"><img src={appImages.jira} className="w-3.5 h-3.5 object-contain"/> Jira</label>
                                    <Select styles={selectStyles} options={jiraOptions} isClearable value={jiraOptions.find(o => o.value === formData.app_mappings.jira?.accountId)} onChange={opt => handleMappingChange('jira', opt)} placeholder="Search..." />
                                    {formData.app_mappings.jira && (
                                        <button type="button" onClick={() => handlePrefill('jira')} className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                                            <ChevronsDown className="w-3 h-3" /> Prefill
                                        </button>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1.5"><img src={appImages.microsoft_teams} className="w-3.5 h-3.5 object-contain"/> Teams</label>
                                    <Select styles={selectStyles} options={teamsOptions} isClearable value={teamsOptions.find(o => o.value === formData.app_mappings.microsoft_teams?.userId)} onChange={opt => handleMappingChange('microsoft_teams', opt)} placeholder="Search..." />
                                    {formData.app_mappings.microsoft_teams && (
                                        <button type="button" onClick={() => handlePrefill('microsoft_teams')} className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                                            <ChevronsDown className="w-3 h-3" /> Prefill
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-3 mt-6 border-t border-zinc-800">
                        <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-semibold transition-colors border rounded-md bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-300">Cancel</button>
                        <button type="submit" disabled={isSubmitting || !!emailError} className="px-3 py-2 text-xs font-semibold text-white transition-all bg-blue-600 rounded-md hover:bg-[#5652CF] disabled:opacity-50">
                            {isSubmitting ? 'Saving...' : 'Save User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Compact Bulk Map Modal ---
const BulkMapModal = ({ allUsers, mappableTeamsUsers, onClose, onSave }) => {
    const { userGoogleId } = DataState();
    const [candidates, setCandidates] = useState([]);
    const [defaultRole, setDefaultRole] = useState('Developer');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        const existingEmails = new Set(allUsers.map(u => u.email.toLowerCase()));
        const existingTeamsIds = new Set(
            allUsers.map(u => u.app_mappings.microsoft_teams?.userId).filter(Boolean)
        );

        const newCandidates = mappableTeamsUsers
            .filter(teamsUser =>
                teamsUser.email && 
                !existingEmails.has(teamsUser.email.toLowerCase()) &&
                !existingTeamsIds.has(teamsUser.userId)
            )
            .map(teamsUser => ({
                displayName: teamsUser.displayName,
                email: teamsUser.email,
                role: defaultRole,
                app_mappings: {
                    microsoft_teams: {
                        userId: teamsUser.userId,
                        displayName: teamsUser.displayName,
                        email: teamsUser.email
                    }
                }
            }));
        setCandidates(newCandidates);
    }, [allUsers, mappableTeamsUsers]);

    const handleCandidateChange = (index, field, value) => {
        const newCandidates = [...candidates];
        newCandidates[index][field] = value;
        setCandidates(newCandidates);
    };

    const handleDefaultRoleChange = (e) => {
        const newRole = e.target.value;
        setDefaultRole(newRole);
        setCandidates(candidates.map(c => ({ ...c, role: newRole })));
    };

    const handleRemoveCandidate = (index) => {
        setCandidates(candidates.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setValidationErrors([]);
        try {
            const res = await fetch(`${API_URL}/users/bulk-map-teams`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userGoogleId}` },
                credentials: 'include',
                body: JSON.stringify(candidates)
            });
            const data = await res.json();
            if (!res.ok) {
                toast.error("Validation failed.");
                setValidationErrors(data.detail || []);
                throw new Error("Validation failed");
            }
            toast.success(data.message);
            onSave();
        } catch (err) {
            // Error handled
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 duration-200 bg-black/80 backdrop-blur-sm animate-in fade-in">
            <div className="bg-[#121214] border border-zinc-700 w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col scale-100 animate-in zoom-in-95">
                <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-900/50 rounded-t-xl">
                    <div>
                        <h3 className="text-lg font-bold text-zinc-100">Bulk Sync</h3>
                        <p className="text-xs text-zinc-500">Found {candidates.length} potential users.</p>
                    </div>
                    <button onClick={onClose} className="p-1 transition-colors rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800"><X className="w-4 h-4"/></button>
                </div>

                <div className="flex items-center gap-3 p-3 border-b bg-zinc-900/30 border-zinc-800">
                    <label className="text-xs font-medium text-zinc-400 whitespace-nowrap">Default Role:</label>
                    <input value={defaultRole} onChange={handleDefaultRoleChange} className="w-48 px-2 py-1 text-xs text-white transition-colors border rounded-md bg-zinc-800 border-zinc-700 focus:outline-none focus:border-[#5652CF]" />
                </div>

                <div className="flex-grow p-0 overflow-auto custom-scrollbar">
                    <table className="w-full text-xs text-left border-collapse">
                        <thead className="sticky top-0 z-10 bg-zinc-900">
                            <tr>
                                <th className="px-4 py-2 font-semibold tracking-wider uppercase text-zinc-500">Display Name</th>
                                <th className="px-4 py-2 font-semibold tracking-wider uppercase text-zinc-500">Email</th>
                                <th className="px-4 py-2 font-semibold tracking-wider uppercase text-zinc-500">Role</th>
                                <th className="px-4 py-2 font-semibold tracking-wider text-center uppercase text-zinc-500">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {candidates.length === 0 ? (
                                <tr><td colSpan="4" className="p-8 text-center text-zinc-500">No new candidates found.</td></tr>
                            ) : candidates.map((candidate, index) => {
                                const error = validationErrors.find(e => e.index === index);
                                return (
                                    <React.Fragment key={candidate.app_mappings.microsoft_teams.userId}>
                                        <tr className="transition-colors hover:bg-zinc-800/30 group">
                                            <td className="p-2 pl-4"><input value={candidate.displayName} onChange={e => handleCandidateChange(index, 'displayName', e.target.value)} className="w-full px-2 py-1 transition-colors border border-transparent rounded bg-zinc-800/50 focus:border-zinc-600 text-zinc-200 focus:outline-none" /></td>
                                            <td className="p-2"><input value={candidate.email} readOnly className="w-full px-2 py-1 bg-transparent cursor-not-allowed text-zinc-500" /></td>
                                            <td className="p-2"><input value={candidate.role} onChange={e => handleCandidateChange(index, 'role', e.target.value)} className="w-full px-2 py-1 transition-colors border border-transparent rounded bg-zinc-800/50 focus:border-zinc-600 text-zinc-200 focus:outline-none" /></td>
                                            <td className="p-2 text-center">
                                                <button onClick={() => handleRemoveCandidate(index)} className="p-1 transition-colors rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5"/></button>
                                            </td>
                                        </tr>
                                        {error && (
                                            <tr className="border-b bg-red-900/10 border-red-900/20">
                                                <td colSpan="4" className="px-4 py-1 text-[10px] text-red-400 flex items-center gap-2">
                                                    <X className="w-3 h-3" /> {error.errors.join(' ')}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between p-4 border-t border-zinc-800 bg-zinc-900 rounded-b-xl">
                    <p className="text-xs font-medium text-zinc-500">{candidates.length} users to create.</p>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-semibold transition-colors border rounded-md bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-300">Cancel</button>
                        <button onClick={handleSubmit} disabled={isSubmitting || candidates.length === 0} className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white transition-all bg-blue-600 rounded-md hover:bg-[#5652CF] disabled:opacity-50">
                            {isSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
                            {isSubmitting ? 'Processing...' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagementContent;